import { FolderContent } from '@/api/generatedApi';
import { createContext, useContext, useState, ReactNode, useMemo, Dispatch, SetStateAction } from 'react';

export interface ContentDragStateValue {
  draggedContents: FolderContent[];
  dropTargetFolderId: string | undefined;
}

interface ContentDragDispatchValue {
  currentFolderId: string | undefined;
  setDraggedContents: Dispatch<SetStateAction<FolderContent[]>>;
  setDropTargetFolderId: Dispatch<SetStateAction<string | undefined>>;
}

const ContentDragStateContext = createContext<ContentDragStateValue | undefined>(undefined);
const ContentDragDispatchContext = createContext<ContentDragDispatchValue | undefined>(undefined);

export const ContentDragProvider = ({
  children,
  currentFolderId,
}: {
  children: ReactNode;
  currentFolderId: string | undefined;
}) => {
  const [draggedContents, setDraggedContents] = useState<FolderContent[]>([]);
  const [dropTargetFolderId, setDropTargetFolderId] = useState<string | undefined>();

  const stateValue = useMemo(() => ({ draggedContents, dropTargetFolderId }), [draggedContents, dropTargetFolderId]);

  const dispatchValue = useMemo(
    () => ({ currentFolderId, setDraggedContents, setDropTargetFolderId }),
    [currentFolderId]
  );

  return (
    <ContentDragDispatchContext.Provider value={dispatchValue}>
      <ContentDragStateContext.Provider value={stateValue}>{children}</ContentDragStateContext.Provider>
    </ContentDragDispatchContext.Provider>
  );
};

export const useContentDragState = () => {
  const context = useContext(ContentDragStateContext);
  if (!context) {
    throw new Error('useContentDragState must be used within a ContentDragProvider');
  }
  return context;
};

export const useContentDragStateOptional = () => {
  return useContext(ContentDragStateContext);
};

export const useContentDragDispatch = () => {
  const context = useContext(ContentDragDispatchContext);
  if (!context) {
    throw new Error('useContentDragDispatch must be used within a ContentDragProvider');
  }
  return context;
};
